import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiCreditCard,
  FiLoader,
  FiAlertCircle,
  FiSave,
} from "react-icons/fi";

import toast from "react-hot-toast";

import {
  getSelfActivateWallets,
  transferSelfActivationAmountToCompanyWallet,
} from "../../store/slices/profile";

const COMPANY_USER_ID = "6ab214fd2e7cec4f71d16df9";

const TransferSelfWallet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    supportIncomeLoading,
    supportIncomeError,
  } = useSelector((state) => state.withdraw);

  const {
    selfActivateWalletsLoading = false,
    selfActivateWalletsError = null,
  } = useSelector((state) => state.profile);

  const [amount, setAmount] = useState("");
  const [remainingPendingAmount, setRemainingPendingAmount] =
    useState(null);
  const [validationError, setValidationError] = useState("");
  const [updating, setUpdating] = useState(false);

  const isLoading =
    selfActivateWalletsLoading ||
    supportIncomeLoading ||
    updating;

  const fetchPendingAmount = async () => {
    try {
      const response = await dispatch(
        getSelfActivateWallets({
          page: 1,
          limit: 10,
        })
      ).unwrap();

      // API response: { status: 1, data: { totalAmountAvailable: 2075 } }
      const data = response?.data ?? response;

      setRemainingPendingAmount(
        Number(data?.totalAmountAvailable ?? 0)
      );
    } catch (error) {
      console.error("FETCH SELF ACTIVATION WALLETS ERROR:", error);
      toast.error(
        typeof error === "string"
          ? error
          : error?.message || "Failed to fetch pending amount."
      );
    }
  };

  useEffect(() => {
    fetchPendingAmount();
  }, [dispatch]);

  const handleAmountChange = (e) => {
    const value = e.target.value;

    // Allow empty input and up to two decimal places.
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setValidationError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    if (remainingPendingAmount === null) {
      setValidationError("Please wait until the pending amount loads.");
      return;
    }

    if (!amount.trim()) {
      setValidationError("Please enter an amount.");
      return;
    }

    const transferAmount = Number(amount);

    if (!Number.isFinite(transferAmount) || transferAmount <= 0) {
      setValidationError("Amount must be greater than zero.");
      return;
    }

    if (transferAmount > remainingPendingAmount) {
      setValidationError(
        `Amount cannot exceed ${remainingPendingAmount.toFixed(2)}.`
      );
      return;
    }

    try {
      setUpdating(true);

      const response = await dispatch(
        transferSelfActivationAmountToCompanyWallet({
          companyUserId: COMPANY_USER_ID,
          amount: transferAmount,
        })
      ).unwrap();

      toast.success(
        response?.message || "Amount transferred successfully."
      );

      setAmount("");

      // Fetch the latest remaining pending amount.
      await fetchPendingAmount();

      navigate(-1);
    } catch (error) {
      console.error("TRANSFER SELF WALLET ERROR:", error);

      toast.error(
        typeof error === "string"
          ? error
          : error?.message || "Failed to transfer amount."
      );
    } finally {
      setUpdating(false);
    }
  };

  const errorMessage =
    validationError || selfActivateWalletsError || supportIncomeError;

  return (
    <div className="gi-referral-page">
      <div className="gi-breadcrumb">
        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span
          onClick={() => navigate(-1)}
          style={{ cursor: "pointer" }}
        >
          Self Activation Wallet
        </span>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>Transfer to Company Wallet</span>
      </div>

      <section className="gi-transfer-card">
        {selfActivateWalletsLoading &&
          remainingPendingAmount === null ? (
          <div className="gi-table-message">
            <FiLoader className="gi-transfer-spinner" />
            Loading remaining amount...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="gi-transfer-form"
          >
            <div className="gi-transfer-form-group">
              <label htmlFor="companyUserId">
                Company User ID
              </label>

              <input
                id="companyUserId"
                type="text"
                value={COMPANY_USER_ID}
                className="gi-transfer-input"
                readOnly
              />
            </div>

            <div className="gi-transfer-form-group">
              <label htmlFor="remainingPendingAmount">
                Remaining Amount
              </label>

              <div className="gi-transfer-input-wrapper">
                <FiCreditCard />

                <input
                  id="remainingPendingAmount"
                  type="text"
                  value={
                    remainingPendingAmount === null
                      ? "Loading..."
                      : remainingPendingAmount.toFixed(2)
                  }
                  className="gi-transfer-input"
                  readOnly
                />
              </div>
            </div>

            <div className="gi-transfer-form-group">
              <label htmlFor="amount">
                Transfer Amount <span>*</span>
              </label>

              <div className="gi-transfer-input-wrapper">
                <FiCreditCard />

                <input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount to transfer"
                  className="gi-transfer-input"
                  disabled={isLoading}
                  min="0.01"
                  max={remainingPendingAmount ?? undefined}
                  step="0.01"
                  inputMode="decimal"
                  required
                />
              </div>

              <small>
                Maximum transferable amount:{" "}
                {remainingPendingAmount === null
                  ? "Loading..."
                  : remainingPendingAmount.toFixed(2)}
              </small>
            </div>

            {errorMessage && (
              <div className="gi-transfer-error">
                <FiAlertCircle />
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              className="gi-transfer-submit"
              disabled={
                isLoading ||
                remainingPendingAmount === null ||
                remainingPendingAmount <= 0 ||
                !amount
              }
            >
              {updating ? (
                <>
                  <FiLoader className="gi-transfer-spinner" />
                  Transferring...
                </>
              ) : (
                <>
                  <FiSave />
                  Transfer to Company Wallet
                </>
              )}
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

export default TransferSelfWallet;
